/**
 * Ein ZIP-Archiv schreiben, ohne Kompression.
 *
 * Gebraucht wird das für genau eine Sache: die KMZ-Datei. KMZ ist ein ZIP mit
 * einer doc.kml darin, und ohne dieses ZIP reisen keine Fotos mit - das ist
 * der einzige Vorteil, den KML gegenüber GPX überhaupt hat.
 *
 * Warum ein eigener Schreiber und keine Bibliothek: dieselbe Überlegung wie
 * beim handgeschriebenen GPX-Leser in lib/gpx.ts. Eine Bibliothek für ZIP kann
 * lesen und schreiben, Kompression in mehreren Stufen, Verschlüsselung, ZIP64,
 * Datenströme - gebraucht wird davon nichts. Was hier steht, ist der
 * Datensatzaufbau aus der Norm (PKWARE APPNOTE, Abschnitte 4.3.7, 4.3.12 und
 * 4.3.16) und eine CRC-32-Prüfsumme, zusammen keine hundert Zeilen. Der Preis
 * einer Abhängigkeit wäre dagegen dauerhaft: sie will aktualisiert werden, sie
 * wandert in das Bündel, das im Funkloch geladen werden muss, und sie ist eine
 * fremde Zeile Code in einer App, die sonst ohne auskommt.
 *
 * Warum ohne Kompression: der Inhalt eines KMZ ist zu 99 Prozent JPEG, und
 * JPEG ist bereits komprimiert - Deflate holt daraus im besten Fall ein
 * Prozent heraus und kostet dafür einen vollständigen Deflate-Kodierer, also
 * ein Mehrfaches dieser Datei. Die Norm sieht die Ablage ohne Kompression
 * (Methode 0) ausdrücklich vor, jedes ZIP-Programm liest sie, und Google Earth
 * öffnet solche KMZ ohne Murren.
 *
 * Alle Zahlen im Archiv stehen in Little-Endian, alle Namen in UTF-8; das
 * Sprachkennzeichen (Bit 11) sagt das den Lesern ausdrücklich, sonst würde ein
 * Umlaut im Dateinamen als Zeichen aus einer DOS-Codepage gelesen.
 */

/** Ein Eintrag des Archivs: Name im Archiv und Inhalt. */
export interface ZipEintrag {
  /**
   * Pfad im Archiv, mit Schrägstrichen getrennt.
   *
   * Ohne führenden Schrägstrich und ohne "..": beides ist in der Norm nicht
   * verboten, aber jeder Entpacker, der es befolgt, schreibt damit ausserhalb
   * des Zielverzeichnisses. Diese Dateien entstehen zwar hier im Programm und
   * nicht aus fremder Hand - eine Datei, die im Ordner des Nutzers landet,
   * schreibt sie trotzdem nicht.
   */
  name: string
  bytes: Uint8Array
}

/** Fehler mit einer Meldung, die man dem Nutzer zeigen kann. */
export class ZipError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ZipError'
  }
}

/**
 * Obergrenzen des klassischen ZIP-Formats.
 *
 * Alles darüber verlangt ZIP64, und das ist ein zweiter Satz Datensätze für
 * einen Fall, den es hier nicht gibt: vier Gigabyte Fotos in einer Reise
 * kämen weder über die Mobilfunkverbindung noch in Google Earth. Erreicht
 * werden sie trotzdem nicht stillschweigend - ein Archiv mit überlaufenen
 * Feldern sieht aus wie ein gültiges und ist keins.
 */
const MAX_BYTES = 0xffffffff
const MAX_EINTRAEGE = 0xffff

/** Signaturen der drei Datensatzarten. */
const SIG_LOKAL = 0x04034b50
const SIG_ZENTRAL = 0x02014b50
const SIG_ENDE = 0x06054b50

/**
 * Fassung, die zum Lesen nötig ist: 2.0.
 *
 * Nicht 1.0, obwohl nichts aus 2.0 benutzt wird. 2.0 ist der Wert, den jedes
 * verbreitete Programm schreibt, und einzelne alte Leser stolpern über
 * niedrigere Angaben.
 */
const VERSION = 20

/** Sprachkennzeichen Bit 11: Name und Kommentar stehen in UTF-8. */
const FLAG_UTF8 = 0x0800

/** Ablage ohne Kompression. */
const METHODE_GESPEICHERT = 0

/** Tabelle für CRC-32, beim ersten Gebrauch gebaut. */
let tabelle: Uint32Array | null = null

function crcTabelle(): Uint32Array {
  if (tabelle) return tabelle

  const t = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) {
      // Das umgedrehte Polynom 0xedb88320, wie es die Norm für CRC-32
      // vorschreibt: dasselbe, das auch PNG und gzip benutzen.
      c = (c & 1) === 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    t[i] = c >>> 0
  }

  tabelle = t
  return t
}

/** CRC-32 einer Bytefolge, wie ZIP sie in jedem Eintrag verlangt. */
export function crc32(bytes: Uint8Array): number {
  const t = crcTabelle()
  let c = 0xffffffff
  for (const b of bytes) {
    c = t[(c ^ b) & 0xff]! ^ (c >>> 8)
  }
  return (c ^ 0xffffffff) >>> 0
}

/**
 * Zeitpunkt als DOS-Datum und DOS-Zeit, wie sie im Eintrag stehen.
 *
 * Das Format stammt aus MS-DOS: Sekunden in Zweierschritten, das Jahr ab 1980.
 * Gerechnet wird in UTC und nicht in der Zeitzone des Geräts - die Datei soll
 * überall dieselbe sein, und ein Zeitstempel im Archiv ist nichts, wonach
 * jemand seine Uhr stellt. Vor 1980 gibt es in diesem Format keine Zeit; ein
 * solcher Zeitpunkt wird auf den 1. Januar 1980 gehoben, statt als negative
 * Jahreszahl in das Feld zu laufen.
 */
export function dosZeit(zeitpunkt: number): { datum: number; zeit: number } {
  const d = new Date(Number.isFinite(zeitpunkt) ? zeitpunkt : 0)
  const jahr = d.getUTCFullYear()
  if (jahr < 1980) return { datum: (1 << 5) | 1, zeit: 0 }

  const datum = ((jahr - 1980) << 9) | ((d.getUTCMonth() + 1) << 5) | d.getUTCDate()
  const zeit =
    (d.getUTCHours() << 11) | (d.getUTCMinutes() << 5) | Math.floor(d.getUTCSeconds() / 2)
  return { datum, zeit }
}

/** Ein Eintrag, fertig gerechnet: Name als Bytes, Prüfsumme, Ablageort. */
interface Vorbereitet {
  nameBytes: Uint8Array
  bytes: Uint8Array
  crc: number
  /** Position des lokalen Datensatzes im Archiv. */
  versatz: number
}

function pruefeName(name: string): void {
  if (name === '') throw new ZipError('Ein Eintrag ohne Namen kann nicht ins Archiv.')
  if (name.startsWith('/') || name.startsWith('\\')) {
    throw new ZipError(`Der Name "${name}" ist ein absoluter Pfad.`)
  }
  if (name.split(/[/\\]/).includes('..')) {
    throw new ZipError(`Der Name "${name}" führt aus dem Archiv heraus.`)
  }
}

/**
 * Packt Einträge in ein ZIP-Archiv.
 *
 * @param eintraege Die Dateien in der Reihenfolge, in der sie im Archiv stehen
 *   sollen. Bei einer KMZ gehört die doc.kml nach vorn: Leser, die nur die
 *   erste Datei ansehen, finden sie dann ohne das Verzeichnis am Ende.
 * @param zeitpunkt Änderungszeit aller Einträge, Millisekunden seit 1970.
 *   Ausdrücklich ein Wert und nicht Date.now(): dieselbe Eingabe soll dasselbe
 *   Archiv ergeben, sonst ist die Datei nicht prüfbar.
 * @throws ZipError bei doppelten oder gefährlichen Namen und wenn das Archiv
 *   die Grenzen des Formats sprengt.
 */
export function zipSchreiben(eintraege: ZipEintrag[], zeitpunkt: number): Uint8Array<ArrayBuffer> {
  if (eintraege.length > MAX_EINTRAEGE) {
    throw new ZipError(
      `Das Archiv hätte ${String(eintraege.length)} Einträge, das Format trägt ${String(MAX_EINTRAEGE)}.`,
    )
  }

  const encoder = new TextEncoder()
  const { datum, zeit } = dosZeit(zeitpunkt)

  // Erst rechnen, dann schreiben: die Gesamtlänge steht damit fest, bevor das
  // erste Byte fällt, und der Puffer wird genau einmal angelegt.
  const gesehen = new Set<string>()
  const vorbereitet: Vorbereitet[] = []
  let versatz = 0
  let zentralLaenge = 0

  for (const eintrag of eintraege) {
    pruefeName(eintrag.name)
    if (gesehen.has(eintrag.name)) {
      throw new ZipError(`Der Name "${eintrag.name}" kommt im Archiv zweimal vor.`)
    }
    gesehen.add(eintrag.name)

    const nameBytes = encoder.encode(eintrag.name)
    vorbereitet.push({ nameBytes, bytes: eintrag.bytes, crc: crc32(eintrag.bytes), versatz })

    versatz += 30 + nameBytes.length + eintrag.bytes.length
    zentralLaenge += 46 + nameBytes.length
    if (versatz > MAX_BYTES) {
      throw new ZipError('Das Archiv wäre grösser als vier Gigabyte, das trägt das Format nicht.')
    }
  }

  const gesamt = versatz + zentralLaenge + 22
  if (gesamt > MAX_BYTES) {
    throw new ZipError('Das Archiv wäre grösser als vier Gigabyte, das trägt das Format nicht.')
  }

  const out = new Uint8Array(gesamt)
  const sicht = new DataView(out.buffer)
  let p = 0

  const u16 = (wert: number) => {
    sicht.setUint16(p, wert, true)
    p += 2
  }
  const u32 = (wert: number) => {
    sicht.setUint32(p, wert >>> 0, true)
    p += 4
  }
  const roh = (bytes: Uint8Array) => {
    out.set(bytes, p)
    p += bytes.length
  }

  // Lokaler Datensatz je Datei, direkt gefolgt vom Inhalt.
  for (const e of vorbereitet) {
    u32(SIG_LOKAL)
    u16(VERSION)
    u16(FLAG_UTF8)
    u16(METHODE_GESPEICHERT)
    u16(zeit)
    u16(datum)
    u32(e.crc)
    // Ohne Kompression sind die beiden Längen dieselbe Zahl.
    u32(e.bytes.length)
    u32(e.bytes.length)
    u16(e.nameBytes.length)
    u16(0)
    roh(e.nameBytes)
    roh(e.bytes)
  }

  // Das zentrale Verzeichnis: dieselben Angaben noch einmal, dazu der Ort des
  // lokalen Datensatzes. Danach sucht jeder Entpacker zuerst.
  const zentralAb = p
  for (const e of vorbereitet) {
    u32(SIG_ZENTRAL)
    // Erzeugende Fassung, dann die zum Lesen nötige.
    u16(VERSION)
    u16(VERSION)
    u16(FLAG_UTF8)
    u16(METHODE_GESPEICHERT)
    u16(zeit)
    u16(datum)
    u32(e.crc)
    u32(e.bytes.length)
    u32(e.bytes.length)
    u16(e.nameBytes.length)
    // Zusatzfeld, Kommentar, Datenträgernummer, innere und äussere Attribute:
    // alles null. Attribute sind Rechte eines Dateisystems, und die hat eine
    // Datei, die im Browser entsteht, nicht.
    u16(0)
    u16(0)
    u16(0)
    u16(0)
    u32(0)
    u32(e.versatz)
    roh(e.nameBytes)
  }

  u32(SIG_ENDE)
  u16(0)
  u16(0)
  u16(vorbereitet.length)
  u16(vorbereitet.length)
  u32(zentralLaenge)
  u32(zentralAb)
  u16(0)

  return out
}
